/** Aspect the explorer can ask for when someone uses Criar igual. */

export type ExploreAspectMode = 'auto' | 'horizontal' | 'vertical';

const KNOWN = ['16:9', '9:16', '1:1', '4:3', '3:4'];

export function isExploreAspectMode(value: unknown): value is ExploreAspectMode {
  return value === 'auto' || value === 'horizontal' || value === 'vertical';
}

/**
 * Horizontal is 16:9, vertical is 9:16.
 * Auto follows the input photos: more portrait frames → 9:16, more landscape → 16:9,
 * all square → 1:1. A tie uses the first photo that has an orientation.
 * With no readable photo, the template's own ratio stays.
 */
export function aspectFromMode(
  mode: ExploreAspectMode,
  sizes: { width: number; height: number }[],
  templateAspect: string,
): string {
  if (mode === 'horizontal') return '16:9';
  if (mode === 'vertical') return '9:16';
  const fallback = KNOWN.includes(templateAspect) ? templateAspect : '16:9';
  const valid = sizes.filter((s) => s.width > 0 && s.height > 0);
  if (!valid.length) return fallback;
  let portrait = 0;
  let landscape = 0;
  for (const size of valid) {
    if (size.height > size.width) portrait += 1;
    else if (size.width > size.height) landscape += 1;
  }
  if (portrait > landscape) return '9:16';
  if (landscape > portrait) return '16:9';
  if (portrait === 0 && landscape === 0) return '1:1';
  const first = valid.find((s) => s.width !== s.height) || valid[0];
  return first.height > first.width ? '9:16' : '16:9';
}

export function exploreAspectHint(
  mode: ExploreAspectMode,
  aspect: string,
  templateAspect: string,
  hasImages: boolean,
): string {
  if (mode === 'horizontal') return 'O vídeo sai na horizontal (16:9).';
  if (mode === 'vertical') return 'O vídeo sai na vertical (9:16).';
  if (!hasImages) return `Sem foto, o vídeo usa a proporção do modelo (${templateAspect}).`;
  if (aspect === '9:16') return 'As fotos estão na vertical. O vídeo sai em 9:16.';
  if (aspect === '1:1') return 'As fotos são quadradas. O vídeo sai em 1:1.';
  return 'As fotos estão na horizontal. O vídeo sai em 16:9.';
}

function u16be(buf: Uint8Array, offset: number): number {
  return (buf[offset] << 8) | buf[offset + 1];
}

function u16le(buf: Uint8Array, offset: number): number {
  return buf[offset] | (buf[offset + 1] << 8);
}

function u32be(buf: Uint8Array, offset: number): number {
  return ((buf[offset] << 24) | (buf[offset + 1] << 16) | (buf[offset + 2] << 8) | buf[offset + 3]) >>> 0;
}

function sizeOf(width: number, height: number): { width: number; height: number } | null {
  if (!width || !height || width > 20000 || height > 20000) return null;
  return { width, height };
}

/** Width and height from the start of a JPEG, PNG, GIF or WebP. Returns null when the header is incomplete. */
export function parseImageSize(buf: Uint8Array): { width: number; height: number } | null {
  if (buf.length >= 24 && buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) {
    return sizeOf(u32be(buf, 16), u32be(buf, 20));
  }
  if (buf.length >= 10 && buf[0] === 0x47 && buf[1] === 0x49 && buf[2] === 0x46) {
    return sizeOf(u16le(buf, 6), u16le(buf, 8));
  }
  if (
    buf.length >= 16
    && buf[0] === 0x52 && buf[1] === 0x49 && buf[2] === 0x46 && buf[3] === 0x46
    && buf[8] === 0x57 && buf[9] === 0x45 && buf[10] === 0x42 && buf[11] === 0x50
  ) {
    const chunk = String.fromCharCode(buf[12], buf[13], buf[14], buf[15]);
    if (chunk === 'VP8X' && buf.length >= 30) {
      const width = 1 + (buf[24] | (buf[25] << 8) | (buf[26] << 16));
      const height = 1 + (buf[27] | (buf[28] << 8) | (buf[29] << 16));
      return sizeOf(width, height);
    }
    if (chunk === 'VP8 ' && buf.length >= 30 && buf[23] === 0x9d && buf[24] === 0x01 && buf[25] === 0x2a) {
      return sizeOf(u16le(buf, 26) & 0x3fff, u16le(buf, 28) & 0x3fff);
    }
    if (chunk === 'VP8L' && buf.length >= 25 && buf[20] === 0x2f) {
      const b1 = buf[21];
      const b2 = buf[22];
      const b3 = buf[23];
      const b4 = buf[24];
      const width = 1 + (((b2 & 0x3f) << 8) | b1);
      const height = 1 + (((b4 & 0x0f) << 10) | (b3 << 2) | ((b2 & 0xc0) >> 6));
      return sizeOf(width, height);
    }
  }
  if (buf.length > 4 && buf[0] === 0xff && buf[1] === 0xd8) {
    let i = 2;
    while (i + 8 < buf.length) {
      if (buf[i] !== 0xff) {
        i += 1;
        continue;
      }
      const marker = buf[i + 1];
      if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
        i += 2;
        continue;
      }
      if (i + 3 >= buf.length) return null;
      const len = u16be(buf, i + 2);
      if (len < 2 || i + 2 + len > buf.length) return null;
      if (marker >= 0xc0 && marker <= 0xc3 && i + 8 < buf.length) {
        return sizeOf(u16be(buf, i + 7), u16be(buf, i + 5));
      }
      i += 2 + len;
    }
  }
  return null;
}
